/* PushNudges — two lightweight opt-in nudges for Web Push:
 *   1. SecondOpenPushPrompt — one-time bottom sheet on the user's 2nd app open.
 *   2. ChatsPushBanner — dismissible bell banner on the Chats inbox.
 * Both no-op when push is unsupported or permission already granted/denied.
 */
import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bell, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { pushSupported, subscribeToPush } from "@/lib/push";

const OPEN_COUNT_KEY = "spark_open_count";
const SECOND_OPEN_ASKED_KEY = "spark_second_open_push_asked";
const BANNER_DISMISS_KEY = "spark_chats_push_banner_dismissed";
const BANNER_COOLDOWN_MS = 14 * 24 * 60 * 60 * 1000;

function permissionIsDefault() {
  return pushSupported() && Notification.permission === "default";
}

/** Counts one "open" per browser session. Returns the running total. */
function bumpOpenCount() {
  try {
    if (sessionStorage.getItem("spark_open_counted") === "1") {
      return Number(localStorage.getItem(OPEN_COUNT_KEY) || 0);
    }
    sessionStorage.setItem("spark_open_counted", "1");
    const next = Number(localStorage.getItem(OPEN_COUNT_KEY) || 0) + 1;
    localStorage.setItem(OPEN_COUNT_KEY, String(next));
    return next;
  } catch {
    return 0;
  }
}

export function SecondOpenPushPrompt({ user }) {
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user || !permissionIsDefault()) return;
    if ((user.email || "").endsWith("@spark.demo")) return;
    try {
      if (localStorage.getItem(SECOND_OPEN_ASKED_KEY) === "1") return;
    } catch { /* show anyway */ }
    if (bumpOpenCount() >= 2) setShow(true);
  }, [user]);

  const markAsked = () => {
    try { localStorage.setItem(SECOND_OPEN_ASKED_KEY, "1"); } catch { /* ignore */ }
  };

  const enable = async () => {
    setBusy(true);
    try {
      await subscribeToPush();
      toast.success("You're all set — we'll ping you the moment someone sparks you ✨");
    } catch {
      toast.message("No worries — you can turn them on anytime in Profile.");
    } finally {
      markAsked();
      setShow(false);
      setBusy(false);
    }
  };

  if (!show) return null;

  return (
    <motion.div
      className="fixed inset-0 z-[60] bg-[#16171A]/85 backdrop-blur-md flex items-end sm:items-center justify-center px-4 pb-8"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      data-testid="second-open-push-prompt"
    >
      <motion.div
        className="w-full max-w-sm bg-[#1E1F23] rounded-[28px] border border-[#C9A961]/30 overflow-hidden p-6"
        initial={{ y: 40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: "spring", stiffness: 400, damping: 30 }}
        style={{ boxShadow: "0 30px 80px rgba(0,0,0,0.7)" }}
      >
        <div className="flex justify-center mb-4">
          <div className="w-14 h-14 rounded-full bg-[#C9A961]/15 border border-[#C9A961]/40 flex items-center justify-center">
            <Bell className="w-6 h-6 text-[#C9A961]" />
          </div>
        </div>
        <h2 className="font-display text-2xl font-medium tracking-tight text-center text-[#F0E6D2] mb-2">
          Never miss a match
        </h2>
        <p className="text-sm text-[#A0A0A8] text-center mb-5 leading-relaxed">
          Matches happen when you're away. Get a quiet ping the moment someone
          likes you back or sends a message.
        </p>
        <button
          onClick={enable}
          disabled={busy}
          className="w-full bg-[#C9A961] text-[#16171A] rounded-full py-3.5 font-bold tracking-wide active:scale-95 transition flex items-center justify-center gap-2 disabled:opacity-60 mb-2"
          data-testid="second-open-push-enable-btn"
        >
          {busy && <Loader2 className="w-4 h-4 animate-spin" />}
          Turn on notifications
        </button>
        <button
          onClick={() => { markAsked(); setShow(false); }}
          className="w-full text-sm font-display italic text-[#A0A0A8] underline underline-offset-4 py-2"
          data-testid="second-open-push-dismiss-btn"
        >
          Maybe later
        </button>
      </motion.div>
    </motion.div>
  );
}

export function ChatsPushBanner() {
  const [visible, setVisible] = useState(() => {
    if (!permissionIsDefault()) return false;
    try {
      const dismissedAt = Number(localStorage.getItem(BANNER_DISMISS_KEY) || 0);
      return !dismissedAt || Date.now() - dismissedAt >= BANNER_COOLDOWN_MS;
    } catch { return true; }
  });
  const [busy, setBusy] = useState(false);

  const enable = async () => {
    setBusy(true);
    try {
      await subscribeToPush();
      toast.success("Notifications on — new messages will find you 🔔");
    } catch {
      toast.message("You can enable them anytime in Profile.");
    } finally {
      setVisible(false);
      setBusy(false);
    }
  };

  const dismiss = () => {
    try { localStorage.setItem(BANNER_DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          className="mx-5 mb-3 flex items-center gap-3 rounded-2xl px-3.5 py-3 bg-gradient-to-r from-[#1B1B1E] to-[#14181A] border border-[#10B981]/25"
          data-testid="chats-push-banner"
        >
          <div className="shrink-0 w-8 h-8 rounded-full bg-[#10B981]/15 border border-[#10B981]/35 flex items-center justify-center">
            <Bell className="w-3.5 h-3.5 text-[#10B981]" />
          </div>
          <button
            onClick={enable}
            disabled={busy}
            className="flex-1 min-w-0 text-left disabled:opacity-60"
            data-testid="chats-push-banner-enable-btn"
          >
            <div className="text-[13px] font-semibold text-[#F0E6D2] leading-tight flex items-center gap-1.5">
              {busy && <Loader2 className="w-3 h-3 animate-spin" />}
              Get pinged when someone replies
            </div>
            <div className="text-[11px] text-[#8C867C] mt-0.5">Tap to turn on notifications</div>
          </button>
          <button
            onClick={dismiss}
            aria-label="Hide notifications banner"
            className="shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-[#8C867C] hover:text-[#F0E6D2] hover:bg-white/[0.06] transition"
            data-testid="chats-push-banner-dismiss-btn"
          >
            <X className="w-3 h-3" />
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
